import { computed, ref } from 'vue';
import { listAssembled } from '@/api/dataHistory';
import type { DataHistoryDetailComponent, DataHistoryRecord } from '@/api/dataHistory';

export type ChangeRecordListItem = DataHistoryRecord & {
  versionNo: number;
};

const PAGE_SIZE = 20;

const OPERATE_TYPE_LABEL: Record<string, string> = {
  INSERT: '新增',
  UPDATE: '修改',
  DELETE: '删除',
};

export function operateTypeLabel(operateType?: string) {
  const key = String(operateType ?? '').trim();
  if (!key) return '';
  return OPERATE_TYPE_LABEL[key] || key;
}

export function operateTypeColor(operateType?: string) {
  const key = String(operateType ?? '').trim();
  if (key === 'INSERT') return 'success';
  if (key === 'UPDATE') return 'processing';
  if (key === 'DELETE') return 'error';
  return 'default';
}

export function useChangeRecordList() {
  const loading = ref(false);
  const loadingMore = ref(false);
  /** 首页加载失败：整页错误态 */
  const errorMsg = ref('');
  /** 翻页失败：保留已加载列表，仅底部提示 */
  const loadMoreErrorMsg = ref('');
  const records = ref<ChangeRecordListItem[]>([]);
  const total = ref(0);
  const current = ref(1);
  const selectedId = ref('');
  const detailComponent = ref<DataHistoryDetailComponent | null>(null);

  let tableCode = '';
  let bizId = '';
  let reqSeq = 0;

  const hasMore = computed(() => records.value.length < total.value);
  const selectedRecord = computed(
    () => records.value.find((item) => item.id === selectedId.value) || null,
  );

  const reset = () => {
    reqSeq += 1;
    loading.value = false;
    loadingMore.value = false;
    errorMsg.value = '';
    loadMoreErrorMsg.value = '';
    records.value = [];
    total.value = 0;
    current.value = 1;
    selectedId.value = '';
    detailComponent.value = null;
    tableCode = '';
    bizId = '';
  };

  const mapPageRecords = (list: DataHistoryRecord[], page: number, pageTotal: number) => {
    return list.map((item, index) => ({
      ...item,
      versionNo: pageTotal - ((page - 1) * PAGE_SIZE + index),
    }));
  };

  const fetchPage = async (page: number, append: boolean) => {
    if (!tableCode || !bizId) return;
    const seq = ++reqSeq;
    if (append) {
      loadingMore.value = true;
      loadMoreErrorMsg.value = '';
    } else {
      loading.value = true;
      errorMsg.value = '';
      loadMoreErrorMsg.value = '';
    }
    try {
      const pageRes = await listAssembled({
        tableCode,
        bizId,
        current: page,
        size: PAGE_SIZE,
        assemble: false,
      });
      if (seq !== reqSeq) return;
      const list = Array.isArray(pageRes?.records) ? pageRes.records : [];
      const pageTotal = Number(pageRes?.total ?? 0) || 0;
      const mapped = mapPageRecords(list, page, pageTotal);
      total.value = pageTotal;
      current.value = page;
      if (!append) {
        detailComponent.value = pageRes?.detailComponent ?? null;
        records.value = mapped;
        selectedId.value = mapped[0]?.id ? String(mapped[0].id) : '';
      } else {
        records.value = records.value.concat(mapped);
      }
    } catch (e: any) {
      if (seq !== reqSeq) return;
      const msg = e?.data?.msg || e?.message || '加载变更记录失败';
      if (append) {
        loadMoreErrorMsg.value = msg;
      } else {
        errorMsg.value = msg;
        records.value = [];
        selectedId.value = '';
        detailComponent.value = null;
      }
    } finally {
      if (seq === reqSeq) {
        loading.value = false;
        loadingMore.value = false;
      }
    }
  };

  const load = (nextTableCode: string, nextBizId: string) => {
    reqSeq += 1;
    tableCode = String(nextTableCode ?? '').trim();
    bizId = String(nextBizId ?? '').trim();
    records.value = [];
    total.value = 0;
    current.value = 1;
    selectedId.value = '';
    detailComponent.value = null;
    errorMsg.value = '';
    loadMoreErrorMsg.value = '';
    fetchPage(1, false);
  };

  const loadMore = () => {
    if (loading.value || loadingMore.value || !hasMore.value) return;
    fetchPage(current.value + 1, true);
  };

  const retry = () => {
    if (!tableCode || !bizId) return;
    fetchPage(1, false);
  };

  const retryLoadMore = () => {
    if (!tableCode || !bizId || loading.value || loadingMore.value || !hasMore.value) return;
    fetchPage(current.value + 1, true);
  };

  const select = (id: string) => {
    selectedId.value = String(id ?? '');
  };

  return {
    loading,
    loadingMore,
    errorMsg,
    loadMoreErrorMsg,
    records,
    total,
    hasMore,
    selectedId,
    selectedRecord,
    detailComponent,
    load,
    loadMore,
    retry,
    retryLoadMore,
    reset,
    select,
  };
}
